import React, { useCallback, useEffect, useState } from "react";
import Select, { TOption } from "../../../Components/Select";
import {
  getDevicesWithValues,
  getDevicesWithStates,
  getStateAttributeValues,
  getDevicesWithTimestamps,
} from "../utils";
import { IDevice } from "../../devices/types";
import {
  defaultAndCondition,
  defaultNoneCondition,
  defaultNumericCondition,
  defaultOrCondition,
  defaultStateChangeCondition,
  defaultStateCondition,
  defaultTimestampCondition,
  TCondition,
} from "./types";
import NumericConditionForm from "./NumericConditionForm";
import StateChangeConditionForm from "./StateChangeConditionForm";
import StateConditionForm from "./StateConditionForm";
import { AndConditionForm, OrConditionForm } from "./LogicalConditionForm";
import TimestampConditionForm from "./TimestampConditionForm";
import { useTranslation } from "react-i18next";

const defaultConditions = {
  none: defaultNoneCondition,
  numeric: defaultNumericCondition,
  timestamp: defaultTimestampCondition,
  state: defaultStateCondition,
  state_change: defaultStateChangeCondition,
  and: defaultAndCondition,
  or: defaultOrCondition,
};

type TConditionType =
  | "numeric"
  | "timestamp"
  | "state"
  | "state_change"
  | "none"
  | "and"
  | "or";

interface IConditionFormProps {
  condition: TCondition;
  devices: IDevice[];
  device_id: number | undefined;
  attribute: string | undefined;
  onChange: (condition: TCondition) => void;
}

const ConditionForm = (props: IConditionFormProps) => {
  const { condition, devices, device_id, attribute, onChange } = props;
  const [initialCondition, setInitialCondition] = useState<TCondition>(
    condition || defaultNoneCondition
  );
  const { t } = useTranslation();
  const conditionTypeOptionsWithoutStateChange: TOption<TConditionType>[] = [
    { value: "none", label: "-" },
    { value: "timestamp", label: t("Timestamp older than") },
    { value: "numeric", label: t("Numeric") },
    { value: "state", label: t("State") },
    { value: "and", label: t("And") },
    { value: "or", label: t("Or") },
  ];
  const allConditionTypeOptions: TOption<TConditionType>[] =
    conditionTypeOptionsWithoutStateChange.concat([
      { value: "state_change", label: t("State Change") },
    ]);

  const [conditionTypeOptions, setConditionTypeOptions] = useState<
    TOption<TConditionType>[]
  >(allConditionTypeOptions);
  const [states, setStates] = useState<string[]>([]);

  useEffect(() => {
    const withStateChange =
      device_id &&
      attribute &&
      getStateAttributeValues(devices, device_id, attribute).length > 0;
    setConditionTypeOptions(
      withStateChange
        ? allConditionTypeOptions
        : conditionTypeOptionsWithoutStateChange
    );
    if (initialCondition.type === "state_change" && !withStateChange) {
      setInitialCondition(defaultNoneCondition);
    }
  }, [device_id, attribute, devices, initialCondition.type]);

  const hasDeviceStateAttributes = useCallback(
    (device_id: number) => {
      const devicesWithStateAttributes = getDevicesWithStates(devices);
      return (
        devicesWithStateAttributes.find((d) => d.id === device_id) !== undefined
      );
    },
    [devices]
  );

  useEffect(() => {
    if (device_id && hasDeviceStateAttributes(device_id) && attribute) {
      const newStates = ["<any>"].concat(
        getStateAttributeValues(devices, device_id, attribute)
      );
      setStates(newStates);
    }
  }, [devices, device_id, attribute, hasDeviceStateAttributes]);

  return (
    <>
      <Select
        fullWidth
        label={t("Condition Type")}
        options={conditionTypeOptions}
        value={initialCondition?.type || "none"}
        onChange={(event) => {
          const newCondition =
            defaultConditions[event.target.value as TConditionType];
          setInitialCondition(newCondition);
          onChange(newCondition);
        }}
        data-cy="condition-select-condition-type"
      />
      {initialCondition.type === "numeric" && (
        <NumericConditionForm
          condition={initialCondition}
          devices={getDevicesWithValues(devices)}
          onChange={onChange}
        />
      )}
      {initialCondition.type === "timestamp" && (
        <TimestampConditionForm
          condition={initialCondition}
          devices={getDevicesWithTimestamps(devices)}
          onChange={onChange}
        />
      )}

      {initialCondition.type === "state" && (
        <StateConditionForm
          condition={initialCondition}
          devices={getDevicesWithStates(devices)}
          onChange={onChange}
        />
      )}
      {initialCondition.type === "state_change" && states.length > 0 && (
        <StateChangeConditionForm
          condition={initialCondition}
          states={states}
          onChange={onChange}
        />
      )}
      {initialCondition.type === "and" && (
        <AndConditionForm
          condition={initialCondition}
          devices={devices}
          device_id={device_id}
          attribute={attribute}
          onChange={onChange}
        />
      )}
      {initialCondition.type === "or" && (
        <OrConditionForm
          condition={initialCondition}
          devices={devices}
          device_id={device_id}
          attribute={attribute}
          onChange={onChange}
        />
      )}
    </>
  );
};

export default ConditionForm;
